import TableHead from "../../../components/@common/TableHead";
import TableBody from "../../../components/@common/TableBody";
import MuiTableCell from "@mui/material/TableCell";
import { SearchBar, useLanguageTranslator } from "../../../components";
import {
  Box,
  Container,
  Paper,
  Stack,
  styled,
  Tab,
  Table,
  TableContainer,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import TabLabel from "../../../components/@common/TabLabel";
import { StatusTag } from "../../../components";
import { useApolloQuery } from "../../../hooks";
import { gql } from "@apollo/client";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

const TableCell = styled(MuiTableCell)({
  textWrap: "nowrap",
});

const GET_SUBMISSIONS = gql`
  query GetSubmissions($after: String, $first: Int) {
    submissions(after: $after, first: $first) {
      edges {
        cursor
        node {
          __typename
          ... on Policy {
            title
            description
            status
            createdAt
          }
          ... on Practice {
            title
            description
            status
            createdAt
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
        hasPreviousPage
        startCursor
      }
      totalCount
    }
  }
`;

function SubmissionsTab() {
  const [inputValue, setInputValue] = useState("");
  const [currTab, setCurrTab] = useState<"all">("all");

  const { translator } = useLanguageTranslator();

  const { data, loading, refetch } = useApolloQuery({
    queryDocument: GET_SUBMISSIONS,
    variables: {
      after: "MQ",
      first: 8,
    },
    isPrivateCall: true,
  });

  const dist = useMemo(() => {
    const dist = {
      approved: [] as any,
      rejected: [] as any,
      pending: [] as any,
    };

    data?.submissions?.edges?.forEach((p: any) => {
      switch (p?.node?.status) {
        case "rejected":
          dist.rejected.push(p);
          break;
        case "approved":
          dist.approved.push(p);
          break;
        case "pending":
        default:
          dist.pending.push(p);
          break;
      }
    });

    return dist;
  }, [data?.submissions?.edges]);

  const privateTab = [
    {
      value: "approved",
      count: dist.approved.length,
      title: translator("status.Approved"),
    },
    {
      value: "pending",
      count: dist.pending.length,
      title: translator("status.Pending"),
    },
    {
      value: "rejected",
      count: dist.rejected.length,
      title: translator("status.Rejected"),
    },
  ];

  const submissionsData = (() => {
    switch (currTab as any) {
      case "approved":
        return dist.approved;
      case "pending":
        return dist.pending;
      case "rejected":
        return dist.rejected;
      default:
        return data?.submissions?.edges || [];
    }
  })();

  const filteredSubmissionsList = useMemo(() => {
    if (!inputValue) return submissionsData || [];

    const searchValue = inputValue
      ?.normalize("NFD")
      ?.replace(/[\u0300-\u036f]/g, "")
      ?.toLowerCase();

    return submissionsData?.filter((item: any) =>
      item?.node?.title
        ?.normalize("NFD")
        ?.replace(/[\u0300-\u036f]/g, "")
        ?.toLowerCase()
        ?.includes(searchValue)
    );
  }, [inputValue, submissionsData]);

  return (
    <>
      <Container sx={{ my: 3 }}>
        <Paper component={Container} sx={{ py: 3, mb: 2 }}>
          <Box mb={2}>
            <Typography variant="h4">
              {translator("Submissions Tab")}
            </Typography>
            <Typography variant="caption">
              {translator(
                "Categorized view of all the submissions you have made"
              )}
              .
            </Typography>
          </Box>

          <Stack
            direction={{ lg: "row" }}
            gap={2}
            justifyContent={{ lg: "space-between" }}
            alignItems={{ lg: "center" }}
          >
            <Tabs
              value={currTab}
              onChange={(_, val) => setCurrTab(val)}
              variant="scrollable"
              sx={{ my: { xs: 1, lg: 0, maxWidth: "1000px" } }}
            >
              {privateTab?.map(({ value, count, title }: any) => (
                <Tab
                  value={value}
                  label={<TabLabel count={count}>{title}</TabLabel>}
                />
              ))}
            </Tabs>

            <Box display={{ xs: "none", lg: "block" }}>
              <SearchBar
                placeholder={translator("Search your submissions")}
                setInputValue={setInputValue}
                inputValue={inputValue}
              />
            </Box>
          </Stack>
        </Paper>

        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>
                    {translator("dashboardTableHead.Category")}
                  </TableCell>
                  <TableCell>{translator("dashboardTableHead.Name")}</TableCell>
                  <TableCell>
                    {translator("dashboardTableHead.Description")}
                  </TableCell>
                  <TableCell>
                    {translator("dashboardTableHead.Status")}
                  </TableCell>
                  <TableCell>
                    {translator("dashboardTableHead.Uploaded date")}
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredSubmissionsList?.map((elem: any, i: any) => (
                  <TableRow key={`${i}`}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{elem?.node?.__typename}</TableCell>
                    <TableCell title={elem?.node?.title}>
                      {elem?.node?.title?.length > 29
                        ? `${elem?.node?.title?.slice(0, 29)}...`
                        : elem?.node?.title}
                    </TableCell>
                    <TableCell>{elem?.node?.description || "-"}</TableCell>
                    <TableCell padding="checkbox">
                      <StatusTag
                        sx={{ padding: "0px" }}
                        label={translator(
                          `status.${
                            elem?.node?.status?.charAt(0)?.toUpperCase() +
                            elem?.node?.status?.slice(1)
                          }`
                        )}
                        status={elem?.node?.status}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {dayjs(
                        elem?.node?.createdAt?.split("T")?.[0] || ""
                      ).format("DD.MM.YYYY")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box component={Container} sx={{ py: 3 }} textAlign="right">
            <Link
              to="/submissions/dashboard/all"
              style={{ textDecoration: "none", color: "black" }}
              color="textSecondary"
            >
              {translator("View all")}
            </Link>
          </Box>
        </Paper>
      </Container>
    </>
  );
}

export { SubmissionsTab };
